import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { opsApi, money } from "../../lib/api";

const FIELDS = [["forecast_to_complete", "Forecast to complete"], ["eac", "Est at completion"],
  ["projected_cost", "Projected cost"]];
const CURRENT = { eac: "eac_override", projected_cost: "projected_cost_override",
  forecast_to_complete: "ftc_override" };

/** A PM override is an opinion about the future, so it is signed: author, note, event. */
export default function OverrideDialog({ project, row, initialField = "eac", onClose, onDone }) {
  const [field, setField] = useState(initialField);
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const current = row[CURRENT[field]];
  const now = row[field];
  const log = row.forecast_log || [];

  const save = async (clear = false) => {
    if (!clear && !note.trim()) { toast.error("A note is required on an override"); return; }
    setBusy(true);
    try {
      await opsApi.budgetOverride(project.id, row.id, {
        field, value: clear ? null : Number(value), note: clear ? "Override cleared" : note.trim(),
      });
      toast.success(clear ? "Override cleared" : "Override saved");
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.detail || "Could not save that override");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
         onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl"
           onClick={(e) => e.stopPropagation()} data-testid="ops-budget-override-dialog">
        <h3 className="text-base font-semibold text-slate-900">
          PM override · {row.cost_code}.{row.cost_type}
        </h3>
        <p className="mt-1 text-xs text-slate-500">{row.cost_code_name} · {row.cost_type_name}</p>

        <div className="mt-4 flex gap-1 rounded-lg bg-slate-100 p-1">
          {FIELDS.map(([k, label]) => (
            <button key={k} onClick={() => { setField(k); setValue(""); }}
                    data-testid={`ops-budget-override-field-${k}`}
                    className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                      field === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="mt-3 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
          <p>Revised budget <span className="font-semibold text-slate-900">{money(row.revised_budget)}</span></p>
          <p>Committed + direct <span className="font-semibold text-slate-900">{money(row.committed + row.direct_cost)}</span></p>
          <p>{FIELDS.find(([k]) => k === field)[1]} now{" "}
            <span className="font-semibold text-slate-900">{money(now)}</span>
            {current !== null && current !== undefined && " (overridden)"}
          </p>
          {field === "forecast_to_complete" && row.forecast_adjustment ? (
            <p>Vendor quote variance so far{" "}
              <span className={`font-semibold ${row.forecast_adjustment > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                {row.forecast_adjustment > 0 ? "+" : ""}{money(row.forecast_adjustment)}</span></p>
          ) : null}
        </div>

        {field === "forecast_to_complete" && log.length > 0 && (
          <ul className="mt-3 max-h-36 space-y-1 overflow-y-auto rounded-lg border border-sky-100 bg-sky-50/50 p-2 text-[11px] text-slate-600"
              data-testid="ops-budget-forecast-log">
            {[...log].reverse().map((e, i) => (
              <li key={i}>
                <span className="text-slate-400">{(e.at || "").slice(0, 10)}</span> · {e.by} ·{" "}
                {e.source === "quote"
                  ? <>quote <b>{e.quote_ref}</b> <span className={e.delta > 0 ? "text-rose-700" : "text-emerald-700"}>{e.delta > 0 ? "+" : ""}{money(e.delta)}</span></>
                  : <>forecast set to <b>{e.value === null ? "—" : money(e.value)}</b></>}
                {e.note && <div className="truncate text-slate-500" title={e.note}>{e.note}</div>}
              </li>
            ))}
          </ul>
        )}

        <label className="mt-3 block text-xs font-medium text-slate-600">New value</label>
        <Input type="number" step="0.01" value={value} onChange={(e) => setValue(e.target.value)}
               placeholder={String(now ?? "")}
               data-testid="ops-budget-override-value" className="mt-1" />

        <label className="mt-3 block text-xs font-medium text-slate-600">Why (required)</label>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="mt-1"
                  placeholder="e.g. labour running 120 hrs over on the corridors"
                  data-testid="ops-budget-override-note" />

        <div className="mt-4 flex items-center justify-end gap-2">
          {current !== null && current !== undefined && (
            <Button variant="outline" size="sm" disabled={busy} onClick={() => save(true)}
                    data-testid="ops-budget-override-clear">Clear override</Button>
          )}
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" disabled={busy || value === ""} onClick={() => save(false)}
                  data-testid="ops-budget-override-save">
            {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Save override
          </Button>
        </div>
      </div>
    </div>
  );
}
